import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import {
  Typography,
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from '@mui/material'

type FormValues = {
  messageSubject: string
  message: string
}

const messageSubjectOptions = [
  'Please select...',
  'Statement, Payments or Charges',
  'Moving home',
  'Estate Services',
  'Adaptations',
  'Anti-Social Behaviour',
  'Property Alterations',
  'Complaints',
  'General Query'
]

const ContactUs: React.FC = () => {
  const [isMessageSent, setIsMessageSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: { messageSubject: 'Please select...', message: '' },
    mode: 'onBlur'
  })

  const onSubmit = () => {
    // In a real app, post to API here. For now, just show success and hide submit.
    setIsMessageSent(true)
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ textTransform: 'uppercase' }}>
        Contact Us
      </Typography>

      <Typography variant="h5" gutterBottom>
        There's lots of ways to get in touch
      </Typography>

      <Typography gutterBottom>
        Why not use our handy form to send us a message, or if you would like to raise a repair,
        please use our <Link to="/myrepairs">report a repair tool</Link>
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <Paper sx={{ maxWidth: 600, width: '100%', p: 3 }}>
          {/* Success banner appears after submission */}
          {isMessageSent && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Your message has been sent to us and we will be in touch soon.
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }} error={!!errors.messageSubject}>
              <InputLabel id="messageSubject-label">What is your message subject</InputLabel>
              <Select
                labelId="messageSubject-label"
                id="messageSubject"
                label="What is your message subject"
                {...register('messageSubject', {
                  validate: (v) => v !== 'Please select...' || 'Please select a valid message subject.'
                })}
              >
                {messageSubjectOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {errors.messageSubject && (
                <FormHelperText>{errors.messageSubject.message}</FormHelperText>
              )}
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={5}
              id="message"
              label="What is your message"
              error={!!errors.message}
              helperText={errors.message?.message}
              sx={{ mb: 3 }}
              {...register('message', {
                required: 'Message is required.',
                minLength: { value: 20, message: 'Message must be at least 20 characters long.' },
                maxLength: { value: 1024, message: 'Message cannot exceed 1024 characters.' }
              })}
            />

            {/* Hide submit button after successful submission */}
            {!isMessageSent && (
              <Button 
                type="submit" 
                variant="contained" 
                disabled={isSubmitting}
                sx={{ textTransform: 'none' }}
              >
                {isSubmitting ? 'Submitting…' : 'Submit'}
              </Button>
            )}
          </Box>
        </Paper>
      </Box>

      <Typography gutterBottom>
        If you'd like to raise a repair, please use the <Link to="/myrepairs">report a repair tool</Link>,
        or if it's an emergency, please call us on the number below.
      </Typography>

      <Typography gutterBottom>
        To speak to our Contact Centre please call 0345 678 0555<br />
        Our Contact Centre is open at the following times:<br />
        Monday to Friday - 8:00am to 6:00pm<br />
        Saturday - Closed<br />
        Sunday - Closed
      </Typography>

      <Typography gutterBottom>
        Our email address is <a href="mailto:customerservices@accentgroup.org">customerservices@accentgroup.org</a>
      </Typography>

      <Typography>
        Or, if you would like to write to us, our address is:<br />
        Accent Housing Ltd<br />
        3rd Floor, Scorex House<br />
        1 Bolton Road<br />
        Bradford<br />
        BD1 4AS
      </Typography>
    </Box>
  )
}

export default ContactUs
